import { useEffect, useState } from 'react';
import { adminListLearners, adminEvents, adminProgressRows, adminPageViews, countBy, weeklyBuckets } from '../../services/adminClient.js';
import { T, card, sectionTitle, eyebrow, PageHeader, StatCard, BarRow, LineChart } from './ui.jsx';

const relabel = (rows, blank) => rows.map(r => ({ ...r, key: r.key === '—' ? blank : r.key }));
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : null);
const fmt = (v) => (v == null ? '—' : `${v}%`);

// Build the funnel for a set of channel events against learners + progress.
function funnel(chEvents, ctx) {
  const visitors = new Set(chEvents.map(e => e.visitor_id).filter(Boolean));
  const learnerIds = new Set();
  chEvents.forEach(e => {
    const lid = e.learner_id || ctx.visitorToLearner[e.visitor_id];
    if (lid && ctx.learnerById[lid]) learnerIds.add(lid);
  });
  const ls = [...learnerIds].map(id => ctx.learnerById[id]);
  const reach = visitors.size;
  const signups = ls.length;
  // Lesson 1 funnel, split Opened → Engaged → Completed. OPENED = the learner has
  // >=1 lesson_progress row (any status) — i.e. they merely opened a lesson.
  // ENGAGED = they actually engaged (durable lesson_1_engaged signal: the stamp,
  // a lesson_1_engaged event, or — for pre-feature learners — a completed lesson).
  // Completed = >=1 completed lesson. The Opened→Engaged gap is the open-and-leave
  // bounce Clarity surfaced. Lesson 3 stays Completed-only.
  const l1Opened  = ls.filter(l => (ctx.startedCount[l.id] || 0) >= 1).length;
  const l1Engaged = ls.filter(l => ctx.engaged[l.id]).length;
  const l1Done    = ls.filter(l => (ctx.completed[l.id]    || 0) >= 1).length;
  const l3Done    = ls.filter(l => (ctx.completed[l.id]    || 0) >= 3).length;
  const paid = ls.filter(l => l.subscription_status === 'active').length;
  const referred = ls.filter(l => l.referral_code && ctx.referredBy.has(l.referral_code)).length;
  // Counts + their conversion %: signup is % of reach; the rest are % of signups.
  return {
    reach, signups, l1Opened, l1Engaged, l1Done, l3Done, paid, referred,
    signupPct: pct(signups, reach),
    l1OpenedPct: pct(l1Opened, signups), l1EngagedPct: pct(l1Engaged, signups), l1DonePct: pct(l1Done, signups),
    l3DonePct: pct(l3Done, signups),
    paidPct: pct(paid, signups), referralPct: pct(referred, signups),
  };
}

function FunnelCard({ title, subtitle, topLabel, color, f }) {
  // Each step: label, count (number of), conversion %. Signup % is of reach;
  // Lesson/Paid/Referral % are of signups.
  const steps = [
    ['Signup',    f.signups,   f.signupPct,    T.amber],
    ['L1 Opened', f.l1Opened,  f.l1OpenedPct,  T.slate],
    ['L1 Engaged',f.l1Engaged, f.l1EngagedPct, T.emerald],
    ['L1 Done',   f.l1Done,    f.l1DonePct,    T.emerald],
    ['L3 Done',   f.l3Done,    f.l3DonePct,    T.emerald],
    ['Paid',      f.paid,      f.paidPct,      T.emerald],
    ['Referral',  f.referred,  f.referralPct,  T.purple],
  ];
  return (
    <div style={{ ...card, flex: '1 1 320px', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <p style={sectionTitle}>{title}</p>
        <span style={{ fontSize: 11, fontWeight: 700, color: color || T.slate }}>{subtitle}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '10px 0 12px' }}>
        <span style={{ fontSize: 28, fontWeight: 800, color: T.ink, letterSpacing: -0.5 }}>{f.reach}</span>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: T.slate }}>{topLabel}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
        {steps.map(([label, count, p, c], i) => (
          <div key={label} style={{ flex: '1 1 0', minWidth: 44, textAlign: 'center', padding: '8px 3px', borderLeft: i === 0 ? 'none' : `1px solid ${T.border}` }}>
            <p style={{ fontSize: 17, fontWeight: 800, color: count ? T.ink : T.slate, margin: 0, lineHeight: 1.1 }}>{count}</p>
            <p style={{ fontSize: 11, fontWeight: 700, color: p == null ? T.slate : c, margin: '1px 0 0' }}>{fmt(p)}</p>
            <p style={{ fontSize: 9.5, fontWeight: 700, color: T.slate, margin: '3px 0 0', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const DIGITAL = [
  { key: 'tiktok',   label: 'TikTok',   color: T.coral,   match: /tiktok|tik[\s_-]?tok/i },
  { key: 'reddit',   label: 'Reddit',   color: T.amber,   match: /reddit/i },
  { key: 'linkedin', label: 'LinkedIn', color: '#0a66c2', match: /linkedin|linked[\s_-]?in/i },
];

export default function AdminOutreach() {
  const [learners, setLearners] = useState(null);
  const [events, setEvents] = useState([]);
  const [progress, setProgress] = useState([]);
  const [views, setViews] = useState([]);

  useEffect(() => {
    let c = false;
    Promise.all([adminListLearners(), adminEvents(90), adminProgressRows(), adminPageViews(90)])
      .then(([l, e, p, v]) => { if (c) return; setLearners(l || []); setEvents(e || []); setProgress(p || []); setViews(v || []); })
      .catch(() => { if (!c) setLearners([]); });
    return () => { c = true; };
  }, []);

  if (!learners) return <p style={{ color: T.slate, fontSize: 13 }}>Loading outreach…</p>;

  // Public-launch floor — opened to the public around 1:45-2:00 PM Central on
  // 2026-06-01. Set to 1:45 PM CT (18:45 UTC) so the real early Facebook signup
  // (~1:55 PM) counts, while overnight/midday testing still drops out. Only
  // count traffic from here forward; the 90-day fetch window applies on top.
  const LAUNCH = Date.parse('2026-06-01T18:45:00Z');
  const since = (ts) => ts != null && Date.parse(ts) >= LAUNCH;
  const launchEvents = events.filter(e => since(e.created_at));
  const launchViews  = views.filter(v => since(v.created_at));

  // Exclude internal traffic (founder/team/test) so the board reflects real
  // visitors only: drop is_internal/is_admin learners, and treat any visitor_id
  // that ever loaded an /admin page as internal (only admins can).
  const realLearners = learners.filter(l => !l.is_internal && !l.is_admin && since(l.created_at));
  const internalVids = new Set(views.filter(v => (v.path || '').startsWith('/admin')).map(v => v.visitor_id).filter(Boolean));

  // Attribution context — built from real, post-launch learners only.
  const learnerById = {}; realLearners.forEach(l => { learnerById[l.id] = l; });
  const visitorToLearner = {}; launchEvents.forEach(e => { if (e.learner_id && e.visitor_id && !visitorToLearner[e.visitor_id]) visitorToLearner[e.visitor_id] = e.learner_id; });
  const completed = {}; progress.forEach(p => { if (p.status === 'completed') completed[p.learner_id] = (completed[p.learner_id] || 0) + 1; });
  // # of lesson_progress rows per learner (any status) = lessons OPENED.
  const startedCount = {}; progress.forEach(p => { if (p.learner_id) startedCount[p.learner_id] = (startedCount[p.learner_id] || 0) + 1; });
  // True Lesson 1 ENGAGEMENT (distinct from merely opened). Three durable
  // sources, unioned: the write-once learners.lesson_1_engaged_at stamp, the
  // lesson_1_engaged event, and — so pre-feature learners aren't undercounted —
  // any completed lesson (completion implies engagement).
  const engaged = {};
  realLearners.forEach(l => { if (l.lesson_1_engaged_at) engaged[l.id] = true; });
  launchEvents.forEach(e => { if (e.event_name === 'lesson_1_engaged' && e.learner_id) engaged[e.learner_id] = true; });
  Object.keys(completed).forEach(id => { engaged[id] = true; });
  const referredBy = new Set(realLearners.map(l => l.referred_by).filter(Boolean));
  const ctx = { learnerById, visitorToLearner, startedCount, completed, engaged, referredBy };

  const sourceText = (e) => `${e.utm_source || ''} ${e.utm_medium || ''} ${e.utm_campaign || ''}`;
  // A scan = a visit tagged ref=qr. The printed QR/NFC code encodes
  // adaptos.app/?ref=qr, so the landing capture stores affiliate_code='qr' on
  // the visitor's events. Also catch any qr/nfc UTM tagging for flexibility.
  const qrEvents = launchEvents.filter(e => /\bqr\b|nfc/i.test(`${e.affiliate_code || ''} ${sourceText(e)}`));
  const qr = funnel(qrEvents, ctx);
  const qrChannels = relabel(countBy(qrEvents.filter(e => e.affiliate_code), 'affiliate_code'), 'Untagged QR');

  // Acquisition breakdowns
  const signupSource = relabel(countBy(realLearners, 'signup_source'), 'Direct / unknown');
  const utmSource    = relabel(countBy(launchEvents.filter(e => e.utm_source), 'utm_source'), 'Other');

  // Overall site volume — first-party page_views beacon (one row per public
  // route load). Admin's own pages are excluded so this reflects visitor
  // traffic, not internal ops usage.
  const publicViews = launchViews.filter(v => !(v.path || '').startsWith('/admin') && !internalVids.has(v.visitor_id));
  const totalViews = publicViews.length;
  const uniqueVisitors = new Set(publicViews.map(v => v.visitor_id).filter(Boolean)).size;
  const viewsTrend = weeklyBuckets(publicViews, 'created_at', 8);
  const topPages = countBy(publicViews, 'path').slice(0, 8);
  // Site-traffic funnel: treat each page view as a visitor touch, then run it
  // through the same funnel() as the channels. Attribution rides on visitor_id,
  // which page_views and events now share (utils/visitorId.js) — a visitor who
  // later signs up carries learner_id on their events, so visitorToLearner maps
  // their page views to the learner. (Historical views logged under the old id
  // won't map; the funnel sharpens as new shared-id traffic flows.)
  const siteFunnel = funnel(publicViews.map(v => ({ visitor_id: v.visitor_id, learner_id: visitorToLearner[v.visitor_id] || null })), ctx);

  // Returning-login retention — of real signups old enough to have had the
  // window, how many were active again (any tracked event >1 day after signup)
  // by Day 2 / Day 7. Activity source is the events table; a pure login with no
  // tracked action isn't captured (noted on the card).
  const DAY = 86400000;
  const eventsByLearner = {};
  launchEvents.forEach(e => { if (e.learner_id) (eventsByLearner[e.learner_id] = eventsByLearner[e.learner_id] || []).push(new Date(e.created_at).getTime()); });
  const retentionCohort = realLearners;
  const retention = (windowDays) => {
    const cutoff = Date.now() - windowDays * DAY;
    let cohort = 0, retained = 0;
    retentionCohort.forEach(l => {
      const signup = l.created_at ? new Date(l.created_at).getTime() : null;
      if (!signup || signup > cutoff) return;   // not eligible — full window hasn't elapsed
      cohort++;
      const ts = eventsByLearner[l.id] || [];
      if (ts.some(t => t >= signup + DAY && t <= signup + windowDays * DAY)) retained++;
    });
    return { cohort, retained, pct: pct(retained, cohort) };
  };
  const d2 = retention(2);
  const d7 = retention(7);

  // ── Activation speed — time from signup to first lesson start ─────
  // first_lesson_started is the only timestamped L1-start signal (lesson_progress
  // carries no timestamp). Median is over real signups who started; the
  // within-window shares use an eligibility cohort (signups old enough for the
  // window to have fully elapsed), mirroring the retention cards above.
  const HOUR = 3600000;
  const firstStartByLearner = {};
  launchEvents.forEach(e => {
    if (e.event_name !== 'first_lesson_started' || !e.learner_id || !learnerById[e.learner_id]) return;
    const t = new Date(e.created_at).getTime();
    if (!(e.learner_id in firstStartByLearner) || t < firstStartByLearner[e.learner_id]) firstStartByLearner[e.learner_id] = t;
  });
  const startDeltas = [];
  realLearners.forEach(l => {
    const signup = l.created_at ? new Date(l.created_at).getTime() : null;
    const start  = firstStartByLearner[l.id];
    if (signup && start && start >= signup) startDeltas.push(start - signup);
  });
  startDeltas.sort((a, b) => a - b);
  const nStarts = startDeltas.length;
  const medianStartMs = nStarts === 0
    ? null
    : (nStarts % 2 ? startDeltas[(nStarts - 1) / 2] : (startDeltas[nStarts / 2 - 1] + startDeltas[nStarts / 2]) / 2);
  const fmtDur = (ms) => {
    if (ms == null) return '—';
    const mins = ms / 60000;
    if (mins < 90) return `${Math.round(mins)} min`;
    const hrs = ms / HOUR;
    if (hrs < 48) return `${Math.round(hrs * 10) / 10} hr`;
    return `${Math.round((hrs / 24) * 10) / 10} days`;
  };
  const startedWithin = (windowMs) => {
    const cutoff = Date.now() - windowMs;
    let cohort = 0, started = 0;
    realLearners.forEach(l => {
      const signup = l.created_at ? new Date(l.created_at).getTime() : null;
      if (!signup || signup > cutoff) return;   // window hasn't fully elapsed for this signup yet
      cohort++;
      const start = firstStartByLearner[l.id];
      if (start && start >= signup && (start - signup) <= windowMs) started++;
    });
    return { cohort, started, pct: pct(started, cohort) };
  };
  const a1h  = startedWithin(HOUR);
  const a24h = startedWithin(24 * HOUR);
  const a48h = startedWithin(48 * HOUR);
  const refHost = (r) => { if (!r) return 'Direct / none'; try { return new URL(r).hostname.replace(/^www\./, ''); } catch { return 'Other'; } };
  const topReferrers = countBy(publicViews.map(v => ({ host: refHost(v.referrer) })), 'host').slice(0, 8);

  return (
    <div>
      <PageHeader title="Outreach" subtitle="Real visitors since public launch (Jun 1, 2026 · ~1:45 PM CT) — internal/test traffic excluded." />

      {/* Overall site volume */}
      <p style={{ ...eyebrow, margin: '0 0 10px' }}>Overall Site Volume — last 90 days</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch', marginBottom: 16 }}>
        <StatCard label="Page Views" value={totalViews} tint={T.emerald} />
        <StatCard label="Unique Visitors" value={uniqueVisitors} tint={T.amber} />
        <div style={{ ...card, flex: '2 1 360px', minWidth: 0 }}>
          <p style={{ ...sectionTitle, marginBottom: 8 }}>Weekly Page Views</p>
          <LineChart points={viewsTrend} color={T.emerald} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 20 }}>
        <FunnelCard title="Site Traffic" subtitle="page views → signup" topLabel="Visitors" color={T.emerald} f={siteFunnel} />
        <div style={{ ...card, flex: '1 1 280px', minWidth: 0 }}>
          <p style={{ ...sectionTitle, marginBottom: 12 }}>Top Pages</p>
          {topPages.length ? <BarRow rows={topPages} color={T.emerald} /> : <p style={{ fontSize: 12, color: T.slate, margin: 0 }}>No page views in the window.</p>}
        </div>
        <div style={{ ...card, flex: '1 1 280px', minWidth: 0 }}>
          <p style={{ ...sectionTitle, marginBottom: 12 }}>Top Referrers</p>
          {topReferrers.length ? <BarRow rows={topReferrers} color={T.purple} /> : <p style={{ fontSize: 12, color: T.slate, margin: 0 }}>No referrer data in the window.</p>}
        </div>
      </div>

      {/* Returning-login retention */}
      <p style={{ ...eyebrow, margin: '0 0 10px' }}>Returning Logins</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch', marginBottom: 6 }}>
        <StatCard label="Back by Day 2" value={fmt(d2.pct)} delta={`${d2.retained} of ${d2.cohort} eligible`} deltaColor={T.slate} tint={T.emerald} />
        <StatCard label="Back by Day 7" value={fmt(d7.pct)} delta={`${d7.retained} of ${d7.cohort} eligible`} deltaColor={T.slate} tint={T.amber} />
      </div>
      <p style={{ fontSize: 11.5, color: T.slate, margin: '0 0 20px', lineHeight: 1.5 }}>
        Of real signups old enough to have had the window, the share active again (any tracked activity) more than a day after signup. Excludes internal/admin accounts. Pure logins with no tracked action aren’t counted yet.
      </p>

      {/* Activation speed — signup → first lesson start */}
      <p style={{ ...eyebrow, margin: '0 0 10px' }}>Activation Speed — Signup → Lesson 1 Start</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch', marginBottom: 6 }}>
        <StatCard label="Median time to start"   value={fmtDur(medianStartMs)} delta={nStarts ? `${nStarts} started` : 'no starts yet'} deltaColor={T.slate} tint={T.emerald} />
        <StatCard label="Started within 1 hour"   value={fmt(a1h.pct)}  delta={`${a1h.started} of ${a1h.cohort} eligible`}  deltaColor={T.slate} tint={T.amber} />
        <StatCard label="Started within 24 hours" value={fmt(a24h.pct)} delta={`${a24h.started} of ${a24h.cohort} eligible`} deltaColor={T.slate} tint={T.amber} />
        <StatCard label="Started within 48 hours" value={fmt(a48h.pct)} delta={`${a48h.started} of ${a48h.cohort} eligible`} deltaColor={T.slate} tint={T.amber} />
      </div>
      <p style={{ fontSize: 11.5, color: T.slate, margin: '0 0 20px', lineHeight: 1.5 }}>
        Median is over real signups who started Lesson 1 (signal: <code style={{ background: '#f1f3f7', padding: '1px 5px', borderRadius: 4 }}>first_lesson_started</code> event). Within-window shares use an eligibility cohort — only signups old enough for the window to have fully elapsed — like the retention cards above. Internal/admin accounts excluded.
      </p>

      {/* QR / NFC funnel */}
      <p style={{ ...eyebrow, margin: '0 0 10px' }}>QR / NFC Funnel</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 20 }}>
        <FunnelCard title="QR / NFC" subtitle="physical → ?ref=qr" topLabel="Scans" color={T.amber} f={qr} />
        <div style={{ ...card, flex: '1 1 280px', minWidth: 0 }}>
          <p style={{ ...sectionTitle, marginBottom: 12 }}>Scans by Source / Channel</p>
          {qrChannels.length ? <BarRow rows={qrChannels} color={T.amber} /> : <p style={{ fontSize: 12, color: T.slate, margin: 0 }}>No QR-tagged scans yet. QR/NFC links carry <code style={{ background: '#f1f3f7', padding: '1px 5px', borderRadius: 4 }}>?ref=&lt;channel&gt;</code> → events.affiliate_code.</p>}
        </div>
      </div>

      {/* Digital outreach funnels */}
      <p style={{ ...eyebrow, margin: '0 0 10px' }}>Digital Outreach Funnels — per-channel GTM</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 20 }}>
        {DIGITAL.map(ch => {
          const chEvents = launchEvents.filter(e => ch.match.test(sourceText(e)));
          return <FunnelCard key={ch.key} title={ch.label} subtitle={`utm_source ~ ${ch.key}`} topLabel="Reach" color={ch.color} f={funnel(chEvents, ctx)} />;
        })}
      </div>

      {/* Acquisition mix */}
      <p style={{ ...eyebrow, margin: '0 0 10px' }}>Acquisition Mix</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ ...card, flex: '1 1 300px' }}>
          <p style={{ ...sectionTitle, marginBottom: 12 }}>Signup Source</p>
          {signupSource.length ? <BarRow rows={signupSource} color={T.amber} /> : <p style={{ fontSize: 12, color: T.slate, margin: 0 }}>No signups yet.</p>}
        </div>
        <div style={{ ...card, flex: '1 1 300px' }}>
          <p style={{ ...sectionTitle, marginBottom: 12 }}>UTM Source (events)</p>
          {utmSource.length ? <BarRow rows={utmSource} color={T.emerald} /> : <p style={{ fontSize: 12, color: T.slate, margin: 0 }}>No UTM-tagged events in the window.</p>}
        </div>
      </div>

      <p style={{ fontSize: 11.5, color: T.slate, margin: '16px 0 0', lineHeight: 1.5 }}>
        Attribution: a channel's <strong>Reach/Scans</strong> = distinct visitors in tagged events; <strong>Signups</strong> = those visitors who became learners (events carry learner_id post-auth). <strong>L1 Opened</strong> = learners with ≥1 lesson_progress row (merely opened a lesson). <strong>L1 Engaged</strong> = true engagement (the <code style={{ background: '#f1f3f7', padding: '1px 5px', borderRadius: 4 }}>lesson_1_engaged</code> signal: 30s dwell / Continue / practice / checkpoint / completion) — the Opened→Engaged gap is the open-and-leave bounce. <strong>L1/L3 Done</strong> = ≥1 / ≥3 completed lessons. Sharpen QR scans with a dedicated qr_scan event + page_views UTM if needed.
      </p>
    </div>
  );
}
